import { Subtype } from '@src/main/modules/parameters/domain/issues/category.interface';
import { SubtypeRepository } from '@src/main/modules/parameters/domain/issues/subtype.repository';
import {
  CreateSubtypeRequest,
  UpdateSubtypeRequest,
} from '@src/main/modules/parameters/infrastructure/dto/createUpdateCategoryRequest';
import {
  SubtypeEntity,
  toSubtype,
} from '@src/main/modules/parameters/infrastructure/entity/category.entity';
import { api, TokenProvider } from '@src/main/shared/infrastructure/entity/axios';

const subtypesPath = '/subtypes';

export const createSubtypeHttpRepository = (tokenProvider: TokenProvider): SubtypeRepository =>
  new SubtypeHttpRepository(tokenProvider);

class SubtypeHttpRepository implements SubtypeRepository {
  private readonly tokenProvider: TokenProvider;

  constructor(tokenProvider: TokenProvider) {
    this.tokenProvider = tokenProvider;
  }

  private async getAuthToken(): Promise<string> {
    const token = await this.tokenProvider.getToken();
    if (!token) throw new Error('Authentication token is missing');
    return token;
  }

  async findAll(): Promise<Subtype[]> {
    const token = await this.getAuthToken();
    const response = await api.get<SubtypeEntity[]>(`${subtypesPath}/all`, token);
    return response.map(toSubtype);
  }

  async findById(id: string): Promise<Subtype> {
    const token = await this.getAuthToken();
    const response = await api.get<SubtypeEntity>(`${subtypesPath}/${id}`, token);
    return toSubtype(response);
  }

  async create(subtype: CreateSubtypeRequest): Promise<Subtype> {
    const token = await this.getAuthToken();
    const response = await api.post<SubtypeEntity>(subtypesPath, token, subtype);
    return toSubtype(response);
  }

  async update(id: string, subtype: UpdateSubtypeRequest): Promise<Subtype> {
    const token = await this.getAuthToken();
    const response = await api.put<SubtypeEntity>(`${subtypesPath}/${id}`, token, subtype);
    return toSubtype(response);
  }

  async delete(id: string): Promise<void> {
    const token = await this.getAuthToken();
    await api.delete(`${subtypesPath}/${id}`, token);
  }
}
